import type { FC, ReactNode } from 'react'

import { useLocale } from '@/i18n'

interface JsonLdProps {
    schema: Record<string, unknown> | Record<string, unknown>[]
}

// The schema builders declare `inLanguage` as a single default-language string. Rewrite it to
// the page's actual language so a German or French page does not advertise itself as English.
// Multi-language values (the site-level `inLanguage` array) are left alone.
function withLocale(value: unknown, locale: string): unknown {
    if (Array.isArray(value)) {
        return value.map((item) => withLocale(item, locale))
    }

    if (value === null || typeof value !== 'object') {
        return value
    }

    const entries = Object.entries(value as Record<string, unknown>).map(([key, nested]) => {
        if (key === 'inLanguage' && typeof nested === 'string') {
            return [key, locale]
        }

        return [key, withLocale(nested, locale)]
    })

    return Object.fromEntries(entries)
}

// Renders a structured-data (schema.org) block as an inline application/ld+json script.
// The `<` escape prevents any CMS-sourced value from breaking out of the script tag.
export const JsonLd: FC<JsonLdProps> = ({ schema }): ReactNode => {
    const locale = useLocale()
    const json = JSON.stringify(withLocale(schema, locale)).replace(/</g, '\\u003c')

    return <script type='application/ld+json' dangerouslySetInnerHTML={{ __html: json }} />
}
